import { useId, useState } from "react";
import { Download, Loader2 } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

import { DeskSelect } from "@/components/itsm-config/desk-select";
import { useDraft } from "@/components/itsm-config/draft-context";
import {
  harmonyTypeLabel,
  type FieldImportResult,
  type ImportableField,
  type ImportableFields,
  type ImportTarget,
} from "@/lib/itsm-config/field-import";

/** Options shown per field before the rest are summarized. */
const OPTIONS_SHOWN = 8;

interface Props {
  tenantId: string;
  /** "Jira fields" / "board columns": what the provider's fields are called. */
  noun: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Picks where the fields come from (a project); omitted when fixed. */
  source?: React.ReactNode;
  /** Shown when there is nothing to list yet, e.g. no project chosen. */
  sourceHint?: string;
  /** The provider's fields for the chosen source; undefined until read. */
  fields?: ImportableFields;
  isLoading: boolean;
  loadError: boolean;
  importing: boolean;
  /** Runs the import; resolves with its result, or null when it failed. */
  onImport: (
    target: ImportTarget,
    deskId: string | null,
    ids: string[],
  ) => Promise<FieldImportResult | null>;
}

/**
 * Pick provider fields and create them as Harmony custom fields - on a desk
 * or tenant-wide - each paired with its provider field both ways. The backend
 * saves the fields and the mappings at once, so unsaved edits must be saved
 * first: the import would otherwise land beside a draft that no longer
 * matches what is stored.
 *
 * Remount it (``key``) when its source changes, so a selection never carries
 * over to fields it was not made from.
 */
export function FieldImportDialog({
  tenantId,
  noun,
  open,
  onOpenChange,
  source,
  sourceHint,
  fields,
  isLoading,
  loadError,
  importing,
  onImport,
}: Props) {
  const { dirty } = useDraft();
  const blockedId = useId();
  const targetLabelId = useId();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [target, setTarget] = useState<ImportTarget>("desk");
  const [deskId, setDeskId] = useState("");
  const [result, setResult] = useState<FieldImportResult | null>(null);

  const listed = fields?.fields ?? [];
  const names = new Map(listed.map((f) => [f.field_id, f.name]));
  const needsDesk = target === "desk" && !deskId;
  const canImport = selected.size > 0 && !needsDesk && !importing;

  const toggle = (field: ImportableField, on: boolean) => {
    setSelected((current) => {
      const next = new Set(current);
      if (on) next.add(field.field_id);
      else next.delete(field.field_id);
      return next;
    });
  };

  const submit = async () => {
    const done = await onImport(target, target === "desk" ? deskId : null, [
      ...selected,
    ]);
    if (done) {
      setResult(done);
      setSelected(new Set());
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
        if (!next) {
          setResult(null);
          setSelected(new Set());
        }
      }}
    >
      <div className="flex items-center gap-2">
        {dirty && (
          <span className="text-muted-foreground text-xs" id={blockedId}>
            Save or discard your changes to import
          </span>
        )}
        <DialogTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            disabled={dirty}
            aria-describedby={dirty ? blockedId : undefined}
          >
            <Download className="mr-1 h-4 w-4" />
            Import {noun}
          </Button>
        </DialogTrigger>
      </div>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Import {noun} as Harmony fields</DialogTitle>
          <DialogDescription>
            Each one becomes a Harmony custom field of the matching type, paired
            both ways: it fills when a record is created and syncs either way
            after. A select keeps following the provider&apos;s options. New
            fields are internal until changed in Settings.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {source}

          {result && <ImportOutcome result={result} names={names} />}

          <div role="status" aria-live="polite">
            {isLoading && (
              <p className="text-muted-foreground flex items-center gap-2 text-sm">
                <Loader2 className="h-4 w-4 animate-spin" /> Reading {noun}...
              </p>
            )}
            {!isLoading && !loadError && !fields && sourceHint && (
              <p className="text-muted-foreground text-sm">{sourceHint}</p>
            )}
            {!isLoading && fields && listed.length === 0 && (
              <p className="text-muted-foreground text-sm">No {noun} found.</p>
            )}
          </div>
          {loadError && (
            <p role="alert" className="text-destructive text-sm">
              Could not read the {noun}.
            </p>
          )}
          {fields?.incomplete && (
            <p className="text-sm text-amber-700">
              Not everything could be read; some fields or options may be
              missing.
            </p>
          )}
          {listed.length > 0 && (
            <ul className="divide-y rounded-md border">
              {listed.map((field) => (
                <FieldRow
                  key={field.field_id}
                  field={field}
                  checked={selected.has(field.field_id)}
                  onToggle={toggle}
                />
              ))}
            </ul>
          )}

          <div className="space-y-2">
            <Label id={targetLabelId} className="text-sm">
              Create the fields
            </Label>
            <RadioGroup
              aria-labelledby={targetLabelId}
              value={target}
              onValueChange={(v) => {
                setTarget(v as ImportTarget);
              }}
              className="flex gap-6"
            >
              <div className="flex items-center gap-2">
                <RadioGroupItem value="desk" id="import-target-desk" />
                <Label htmlFor="import-target-desk">On a desk</Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="global" id="import-target-global" />
                <Label htmlFor="import-target-global">Tenant-wide</Label>
              </div>
            </RadioGroup>
            {target === "desk" ? (
              <>
                <DeskSelect
                  tenantId={tenantId}
                  value={deskId}
                  onChange={setDeskId}
                  aria-label="Desk"
                />
                {needsDesk && (
                  <p className="text-muted-foreground text-xs">
                    Choose the desk the fields are created on.
                  </p>
                )}
              </>
            ) : (
              <p className="text-muted-foreground text-xs">
                Every desk holds a tenant-wide field, so its name must not be
                used by any desk.
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            onClick={() => {
              void submit();
            }}
            disabled={!canImport}
          >
            {importing && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
            Import {selected.size > 0 ? String(selected.size) : ""}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FieldRow({
  field,
  checked,
  onToggle,
}: {
  field: ImportableField;
  checked: boolean;
  onToggle: (field: ImportableField, on: boolean) => void;
}) {
  const id = `import-${field.field_id}`;
  const why = field.importable ? null : (field.detail ?? field.reason ?? null);
  const options = field.options ?? [];
  const shown = options.slice(0, OPTIONS_SHOWN).join(", ");
  const more = options.length - OPTIONS_SHOWN;
  return (
    <li className="flex items-start gap-3 px-3 py-2">
      <Checkbox
        id={id}
        disabled={!field.importable || field.imported}
        checked={checked}
        aria-describedby={why ? `${id}-why` : undefined}
        onCheckedChange={(on) => {
          onToggle(field, on === true);
        }}
      />
      <div className="min-w-0 flex-1">
        <Label htmlFor={id} className="text-sm font-medium">
          {field.name}
          <span className="text-muted-foreground ml-1 font-normal">
            ({field.field_id})
          </span>
        </Label>
        <p className="text-muted-foreground text-xs">
          {harmonyTypeLabel(field)}
          {options.length > 0 && `: ${shown}`}
          {more > 0 && ` +${String(more)} more`}
        </p>
        {why && (
          <p id={`${id}-why`} className="text-muted-foreground text-xs">
            {why}
          </p>
        )}
      </div>
      {field.imported && (
        <Badge variant="secondary" className="text-xs">
          Imported
        </Badge>
      )}
    </li>
  );
}

function ImportOutcome({
  result,
  names,
}: {
  result: FieldImportResult;
  names: Map<string, string>;
}) {
  return (
    <div
      role="status"
      className="space-y-1 rounded-md border px-3 py-2 text-sm"
    >
      {result.imported.map((r) => (
        <p key={r.field_id}>
          {r.label}: {r.created ? "created and paired" : "already imported"}
        </p>
      ))}
      {result.skipped.map((s) => (
        <p key={s.field_id} className="text-destructive">
          {names.get(s.field_id) ?? s.field_id}: {s.detail || s.reason}
        </p>
      ))}
    </div>
  );
}
